import mapsScenario from './maps';
import { subDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { mockFederatedGraphql } from 'garaje/mirage/helpers/mock-graphql';

/**
 * Sets up Maps Mirage Scenario
 * - `MIRAGE_SCENARIO=maps yarn start:mirage`
 *
 * @param {object} server   Mirage Server
 * @param {object} options  Options for visitors-classic scenario
 */

export function createDeskMapFeature(server, employee, gqlMapFloor) {
  const graphqlEmployee = server.schema['graphql/employees'].find(employee.id);
  const deskMapFeature = server.create('graphql/desk-map-feature');
  const gqlMapFeature = server.create('graphql/map-feature', { type: 'DESK', floor: gqlMapFloor });
  const mapFeature = server.schema.mapFeatures.find(gqlMapFeature.id);

  const desk = server.create('desk', {
    floor: server.schema.floors.find(gqlMapFloor.id),
    deskAvailability: 'userAssigned',
    assignedTo: employee.email,
  });

  mapFeature.update({
    externalId: desk.id,
  });

  const utilization = server.create('graphql/desk-utilization', { percentUtilized: 0.5 });
  const gqlDesk = server.create('graphql/desk', {
    name: desk.name,
    mapFeature: gqlMapFeature,
    assignedEmployee: graphqlEmployee,
    utilization,
    deskAvailability: 'userAssigned',
  });

  deskMapFeature.update({
    type: 'desk',
    id: mapFeature.id,
    name: desk.name,
    desk: gqlDesk,
  });

  return deskMapFeature;
}

function createUnassignedEmployees(server, count = 10) {
  const unassignedEmployees = [];
  for (let i = 0; i < count; i++) {
    const meetsAttendancePolicy = i % 2 === 0;
    const employee = server.create('employee', {
      locationIds: [1],
    });
    const ninetyDaysAgo = subDays(new Date(), 90);
    const gqlEmployee = server.create('graphql/employee', {
      id: employee.id,
      name: employee.name,
      department: employee.department,
      email: employee.email,
      assignedDesks: null,
      attendancePerformance: server.create('graphql/attendance-performance', {
        onsiteMission: server.create('graphql/onsite-mission', {
          department: 'Engineering',
          created: format(ninetyDaysAgo, 'yyyyMMdd'),
          mission: 3,
          period: 'WEEK',
        }),
        insights: server.create('graphql/employee-attendance-insight', {
          meetsAttendancePolicy: meetsAttendancePolicy,
        }),
        averageWeeklyAttendance: 3.8,
      }),
    });
    unassignedEmployees.push(gqlEmployee);
  }
  return unassignedEmployees;
}

function setupAssignedDesks(server, deskCount, gqlMapFloor) {
  const gqlDesks = [];
  const assignedEmployees = [];

  // create 10 desks that are assigned to employees
  for (let i = 0; i < deskCount; i++) {
    const meetsAttendancePolicy = i % 2 === 0;
    const employee = server.create('employee', {
      locationIds: [1],
    });
    // we need to create a 'regular' desk record too, since that is what's on the map
    const desk = server.create('desk', {
      floor: server.schema.floors.find(gqlMapFloor.id),
      deskAvailability: 'userAssigned',
      assignedTo: employee.email,
    });

    const deskMapFeature = server.create('graphql/desk-map-feature', 'asAssignedDesk', { desk });
    const ninetyDaysAgo = subDays(new Date(), 90);

    const graphqlEmployee = server.create('graphql/employee', {
      id: employee.id,
      name: employee.name,
      email: desk.assignedTo,
      department: employee.department,
      assignedDesks: [deskMapFeature.desk],
      attendancePerformance: server.create('graphql/attendance-performance', {
        onsiteMission: server.create('graphql/onsite-mission', {
          department: 'Engineering',
          created: format(ninetyDaysAgo, 'yyyyMMdd'),
          mission: 3,
          period: 'WEEK',
        }),
        insights: server.create('graphql/employee-attendance-insight', {
          meetsAttendancePolicy: meetsAttendancePolicy,
        }),
        averageWeeklyAttendance: 3.4,
      }),
    });

    gqlDesks.push(deskMapFeature.desk);
    assignedEmployees.push(graphqlEmployee);
  }

  return assignedEmployees;
}

export function setupGraphqlMocks(
  server,
  GqlMapFloor,
  assignedEmployees,
  unassignedEmployees,
  employeePopulatedByScim = false
) {
  const GqlAreaMap = server.create('graphql/area-map', { floors: [GqlMapFloor] });
  const gqlEmployeePopulatedByScim = server.create('graphql/assigned-and-unassigned-employees-meta-type', {
    employeesPopulatedByScim: employeePopulatedByScim,
  });

  mockFederatedGraphql(server, {
    Query: {
      getAssignedAndUnassignedEmployees(_obj, _args, _context) {
        return {
          assignedEmployees: assignedEmployees,
          unassignedEmployees: unassignedEmployees,
          meta: gqlEmployeePopulatedByScim,
        };
      },
      areaMap(_obj, _args, _context) {
        const obj = {
          id: GqlAreaMap.id,
          floors: [
            {
              id: GqlMapFloor.id,
              features: GqlMapFloor.features.models.filter((feature) => feature != null),
            },
          ],
        };

        return obj;
      },
    },
  });
}

function createPointsOfInterest(server) {
  return [
    server.create('graphql/map-feature', { type: 'AED', name: 'AED' }),
    server.create('graphql/map-feature', { type: 'EMERGENCY_EXIT', name: 'Emergency exit' }),
    server.create('graphql/map-feature', { type: 'FIRE_EXTINGUISHER', name: 'Fire extinguisher' }),
    server.create('graphql/map-feature', { type: 'FIRST_AID_KIT', name: 'First aid' }),
    server.create('graphql/map-feature', { type: 'BREAK_ROOM', name: 'Break room' }),
    server.create('graphql/map-feature', { type: 'CAFE', name: 'Cafe' }),
    server.create('graphql/map-feature', { type: 'DELIVERY_AREA', name: 'Delivery area' }),
    server.create('graphql/map-feature', { type: 'KITCHEN', name: 'Kitchen' }),
    server.create('graphql/map-feature', { type: 'MOTHERS_ROOM', name: 'Mothers room' }),
    server.create('graphql/map-feature', { type: 'PRINTER', name: 'Printer' }),
    server.create('graphql/map-feature', { type: 'VISITOR_AREA', name: 'Visitor area' }),
    server.create('graphql/map-feature', { type: 'ACCESSIBLE', name: 'Accessible' }),
    server.create('graphql/map-feature', { type: 'ELEVATOR', name: 'Elevator' }),
    server.create('graphql/map-feature', { type: 'RESTROOM', name: 'Restroom' }),
    server.create('graphql/map-feature', { type: 'STAIRS', name: 'Stairs' }),
    server.create('graphql/map-feature', { type: 'CUSTOM_FEATURE', name: 'Custom feature' }),
  ];
}

export default function (server) {
  mapsScenario(server, { areaMapTrait: 'withMapFloors' });

  const rooms = server.createList('room', 5, { active: true, locationId: 1 });
  const mapFloor = server.schema.mapFloors.first();
  const gqlMapFloor = server.create('graphql/map-floor', { id: mapFloor.id, name: mapFloor.name });

  server.create('graphql/employee', { name: 'John Doe', email: 'john@doe.com' });

  const roomFeatures = rooms.map((room) =>
    server.create('map-feature', 'asRoom', { mapFloor, externalId: room.id, name: room.displayName })
  );

  const roomMapFeatures = roomFeatures.map((roomFeature) =>
    server.create('graphql/room-map-feature', { id: roomFeature.id })
  );

  // populates the gqlDesks and assignedEmployees array
  const assignedEmployees = setupAssignedDesks(server, 5, gqlMapFloor);
  const unassignedEmployees = createUnassignedEmployees(server);
  const deskMapFeatures = server.schema['graphql/deskMapFeatures'].all().models;
  const hotelDesk = server.create('desk', {
    floor: server.schema.floors.find(gqlMapFloor.id),
    deskAvailability: 'available',
    neighborhood: null,
    assignedTo: null,
  });
  const hotelDeskMapFeature = server.create('graphql/desk-map-feature', 'asHotelDesk', { desk: hotelDesk });

  const pointOfInterests = createPointsOfInterest(server);

  const GqlMapFloor = server.create('graphql/map-floor', {
    features: [...deskMapFeatures, hotelDeskMapFeature, ...roomMapFeatures, ...pointOfInterests],
  });

  setupGraphqlMocks(server, GqlMapFloor, assignedEmployees, unassignedEmployees);
  return { gqlAssignedEmployees: assignedEmployees, gqlUnassignedEmployees: unassignedEmployees };
}
